import { useEffect, useRef, MutableRefObject } from 'react';
import { Subject, Observable } from 'rxjs';
import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import {
	DlobWebsocketClient,
	ENUM_UTILS,
	MarketId,
	OrderbookGrouping,
} from '@velocity-exchange/common';
import { useQueryClient } from '@tanstack/react-query';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { getOrderbookGroupingForMarket } from 'src/constants/marketDecimalConfig';
import useGetTickSizeForMarket from 'src/hooks/useGetTickSizeForMarket';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import { useShallow } from 'zustand/react/shallow';
import { dlog } from 'src/dev';
import { orderbookQueryKeys } from 'src/hooks/query/orderbook/orderbookQueryKeys';
import { L2OrderbookWithSlot } from 'src/hooks/query/orderbook/types';
import { TransformedMarketData } from '../utils/transformMarketData';
import { publishMarketLiveUpdate } from 'src/stores/marketLiveState';

interface UseOrderbookSubscriptionParams {
	client: DlobWebsocketClient;
	subscriptionRef: MutableRefObject<{ unsubscribe: () => void } | null>;
	currentSubscriptionMarketIdRef: MutableRefObject<MarketId | null>;
	currentSubscriptionGroupingRef: MutableRefObject<OrderbookGrouping | null>;
	destroyCurrentPipeline$: Subject<void>;
	createDataPipeline: (marketId: MarketId) => Observable<TransformedMarketData>;
	subscriptionRestartKey: number;
}

/**
 * Hook that manages the main orderbook subscription effect.
 * Sets up the data pipeline and handles market/grouping changes.
 */
export function useOrderbookSubscription({
	client,
	subscriptionRef,
	currentSubscriptionMarketIdRef,
	currentSubscriptionGroupingRef,
	destroyCurrentPipeline$,
	createDataPipeline,
	subscriptionRestartKey,
}: UseOrderbookSubscriptionParams): void {
	const queryClient = useQueryClient();
	const selectedMarket = useVelocityStore((s) => s.selectedMarket);
	const setMarketStateStore = useMarketStateStore((s) => s.set);
	const getTickSizeForMarket = useGetTickSizeForMarket();

	const { groupingSizeSelection, orderbookSource, disableAutoSourceSwitching } =
		useOrderbookDisplayStore(
			useShallow((s) => ({
				groupingSizeSelection: s.groupingSizeSelection,
				orderbookSource: s.orderbookSource,
				disableAutoSourceSwitching: s.disableAutoSourceSwitching,
			}))
		);

	const {
		setOrderbookStore,
		resetOrderbookStore,
		setLoading,
		setWaitingForNewGroupingData,
	} = useOrderbookDisplayStore(
		useShallow((s) => ({
			setOrderbookStore: s.set,
			resetOrderbookStore: s.reset,
			setLoading: s.setLoading,
			setWaitingForNewGroupingData: s.setWaitingForNewGroupingData,
		}))
	);

	/**
	 * Which client the tracked subscription actually lives on. The market/grouping
	 * refs alone cannot tell a live subscription from one left behind on a
	 * discarded client, so without this the "already subscribed" check below can
	 * short-circuit after the client is swapped and leave the new one unsubscribed.
	 */
	const subscribedClientRef = useRef<DlobWebsocketClient | null>(null);

	const groupingSizeSelectionRef = useSyncedRef(groupingSizeSelection);
	const orderbookSourceRef = useSyncedRef(orderbookSource);
	const disableAutoSourceSwitchingRef = useSyncedRef(
		disableAutoSourceSwitching
	);

	useEffect(() => {
		if (!selectedMarket.marketId) {
			// Clean up pipeline before unsubscribing
			destroyCurrentPipeline$.next();
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			if (currentSubscriptionMarketIdRef.current) {
				client.unsubscribeAll();
				currentSubscriptionMarketIdRef.current = null;
			}
			currentSubscriptionGroupingRef.current = null;
			resetOrderbookStore();
			return;
		}

		// Clean up websocket subscription when source is not websocket
		// (either manual switch via dev tools, or auto-fallback from timeout)
		if (
			!ENUM_UTILS.match(orderbookSourceRef.current, OrderbookSource.WEBSOCKET)
		) {
			// Clean up pipeline before unsubscribing
			destroyCurrentPipeline$.next();
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			if (currentSubscriptionMarketIdRef.current) {
				client.unsubscribeAll();
				currentSubscriptionMarketIdRef.current = null;
			}
			currentSubscriptionGroupingRef.current = null;
			return;
		}

		const marketId = selectedMarket.marketId;

		// Check if already subscribed
		if (
			subscribedClientRef.current === client &&
			currentSubscriptionMarketIdRef.current?.equals(marketId) &&
			currentSubscriptionGroupingRef.current ===
				groupingSizeSelectionRef.current
		) {
			return;
		}

		// Clean up previous
		if (subscriptionRef.current) {
			destroyCurrentPipeline$.next();
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}

		// Determine grouping
		let newGrouping = groupingSizeSelectionRef.current;
		const storeMarketId = useOrderbookDisplayStore.getState().marketId;
		const isTabRecovery =
			!currentSubscriptionMarketIdRef.current &&
			storeMarketId?.equals(marketId);

		if (
			!isTabRecovery &&
			!currentSubscriptionMarketIdRef.current?.equals(marketId)
		) {
			const baseAssetSymbol =
				selectedMarket.current?.uiSymbols.baseAssetSymbol ?? '';
			const tickSizeBN = getTickSizeForMarket(marketId);
			const tickSizeInDollars = BigNum.from(
				tickSizeBN,
				PRICE_PRECISION_EXP
			).toNum();
			const isValidTickSize = tickSizeInDollars > 0.000001;
			newGrouping = getOrderbookGroupingForMarket(
				baseAssetSymbol,
				isValidTickSize ? tickSizeInDollars : undefined
			);
		}

		// Update store
		setOrderbookStore((s) => {
			const isMarketChange = !s.marketId?.equals(marketId);
			const isGroupingChange = s.groupingSizeSelection !== newGrouping;

			if (isMarketChange) {
				s.marketId = marketId;
				s.loading = true;
				s.waitingForNewGroupingData = false;
				s.groupingSizeSelection = newGrouping;
			} else if (isGroupingChange) {
				s.waitingForNewGroupingData = true;
			}
		});

		// Subscribe
		client.subscribeToMarkets([{ marketId, grouping: newGrouping }]);
		client.resetSlotTracking();

		currentSubscriptionMarketIdRef.current = marketId;
		currentSubscriptionGroupingRef.current = newGrouping;
		subscribedClientRef.current = client;

		// Set up pipeline
		const subscription = createDataPipeline(marketId).subscribe({
			next: ({
				l2Orderbook,
				marketPriceState,
				oracleData,
				mmOracleData,
				slot,
			}) => {
				if (
					ENUM_UTILS.match(
						orderbookSourceRef.current,
						OrderbookSource.WEBSOCKET
					)
				) {
					const l2OrderbookWithSlot: L2OrderbookWithSlot = {
						...l2Orderbook,
						slot,
						oraclePrice: BigNum.from(
							(mmOracleData ?? oracleData).price,
							PRICE_PRECISION_EXP
						),
					};
					queryClient.setQueryData(
						orderbookQueryKeys.l2Orderbook(marketId.key),
						l2OrderbookWithSlot
					);
					queryClient.setQueryData(
						orderbookQueryKeys.marketPriceState(marketId.key),
						marketPriceState
					);

					setLoading(false);
					setWaitingForNewGroupingData(false);

					// Live prices are owned by the market live-state module; the
					// store now keeps only orderbook-derived best bid/ask + slot.
					publishMarketLiveUpdate('dlob', marketId, {
						oracle: {
							price: BigNum.from(oracleData.price, PRICE_PRECISION_EXP),
							slot: oracleData.slot.toNumber(),
							confidence: oracleData.confidence,
							hasSufficientNumberOfDataPoints:
								oracleData.hasSufficientNumberOfDataPoints,
							twap: BigNum.from(oracleData.twap, PRICE_PRECISION_EXP),
							twapConfidence: oracleData.twapConfidence,
							maxPrice: BigNum.from(oracleData.maxPrice, PRICE_PRECISION_EXP),
							raw: oracleData,
						},
						mark: {
							price: marketPriceState.markPrice,
							slot: l2Orderbook.slot ?? slot,
						},
						mmOracle: mmOracleData
							? {
									price: BigNum.from(mmOracleData.price, PRICE_PRECISION_EXP),
									slot: mmOracleData.slot.toNumber(),
									confidence: mmOracleData.confidence,
									hasSufficientNumberOfDataPoints:
										mmOracleData.hasSufficientNumberOfDataPoints,
									isMMOracleActive: mmOracleData.isMMOracleActive,
									raw: mmOracleData,
								}
							: undefined,
					});

					setMarketStateStore((s) => {
						s.latestUpdateSlot = Math.max(
							s.latestUpdateSlot,
							oracleData.slot.toNumber()
						);
						s.marketDataState[marketId.key] = {
							...s.marketDataState[marketId.key],
							derivedState: {
								...s.marketDataState[marketId.key]?.derivedState,
								bestBid: marketPriceState.bestBid,
								bestAsk: marketPriceState.bestAsk,
								updateSlot: oracleData.slot.toNumber(),
							},
						};
					});
				}
			},
			error: (error) => {
				dlog('orderbook_websocket_subscription', 'Subscription error', {
					error,
				});
			},
		});

		subscriptionRef.current = subscription;
	}, [
		selectedMarket.marketId?.key,
		client,
		resetOrderbookStore,
		createDataPipeline,
		setOrderbookStore,
		setLoading,
		setWaitingForNewGroupingData,
		groupingSizeSelection,
		orderbookSource,
		disableAutoSourceSwitching,
		subscriptionRestartKey,
		setMarketStateStore,
		queryClient,
		destroyCurrentPipeline$,
		getTickSizeForMarket,
		subscriptionRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		groupingSizeSelectionRef,
		orderbookSourceRef,
		disableAutoSourceSwitchingRef,
	]);
}
